import { Icon } from '@gftg/ui';
import { useEffect, useState } from 'react';
import { EmptyBlock, ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { assetUrl } from '../lib/api';
import { formatFeedTimestamp } from '../lib/datetime';
import { useAnnouncements, useEvent, type Announcement } from '../lib/queries';
import { useReadAnnouncements } from '../lib/readAnnouncements';
import { useNow } from '../lib/useNow';

/**
 * The announcement feed (module 8). Newest first, and only what the organizers have actually sent
 * — a draft and a schedule are both invisible, because an announcement is news the moment it is
 * readable and there is no way to un-send one.
 *
 * What is in the list is decided by the API, not here: ANN-02 sends some of them to one group, and
 * a screen that filtered client-side would be a screen that had already been given the sponsor
 * breakfast.
 */
export function AnnouncementsPage() {
  const event = useEvent();
  const feed = useAnnouncements();
  const now = useNow(event.data?.serverTime);
  const { isRead, markRead } = useReadAnnouncements();

  const announcements = feed.data?.announcements ?? [];

  /*
   * ANN-03 wants the unread ones marked in the feed, and opening the feed is what reads them —
   * so which ones were new is remembered for this visit while the store is updated straight away.
   * The dots therefore stay put while they are being read, and the home screen's count clears at
   * the same moment, without either depending on when this screen happens to unmount.
   *
   * It is a snapshot per visit: an announcement that lands while the screen is open arrives
   * without a dot, which is the harmless direction to be wrong in.
   */
  const [unreadOnArrival, setUnreadOnArrival] = useState<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (!feed.data || unreadOnArrival) {
      return;
    }

    const ids = feed.data.announcements.map((announcement) => announcement.id);
    setUnreadOnArrival(new Set(ids.filter((id) => !isRead(id))));
    markRead(ids);
  }, [feed.data, unreadOnArrival, isRead, markRead]);

  return (
    <>
      <div className="border-b border-border-2 bg-surface">
        <PageHeader title="Announcements" />
      </div>

      <main className="px-gutter space-y-card py-4">
        {(feed.isPending || event.isPending) && <LoadingBlock label="Loading announcements…" />}

        {/* The event is needed as well as the feed: every card is timestamped in the venue's own
            timezone, so without it there is nothing to render rather than a list without times. */}
        {(feed.isError || event.isError) && (
          <ErrorBlock
            message="The announcements could not be loaded."
            onRetry={() => {
              void feed.refetch();
              void event.refetch();
            }}
          />
        )}

        {feed.data && announcements.length === 0 && (
          <EmptyBlock title="Nothing announced yet">
            Room changes, meal times and shuttle departures arrive here during the event — and by
            email, so you will not miss one while your phone is in your bag.
          </EmptyBlock>
        )}

        {event.data &&
          announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              timezone={event.data.timezone}
              now={now}
              isUnread={unreadOnArrival?.has(announcement.id) ?? false}
            />
          ))}
      </main>
    </>
  );
}

interface CardProps {
  announcement: Announcement;
  timezone: string;
  now: Date;
  isUnread: boolean;
}

function AnnouncementCard({ announcement, timezone, now, isUnread }: CardProps) {
  const { audienceLabel, accentColor, imageAssetId, ctaLabel, ctaUrl } = announcement;
  const image = assetUrl(imageAssetId);

  return (
    <article
      className="rounded-card overflow-hidden border border-border bg-surface"
      // The organizers colour-code these, and the colour arrives as a validated hex or not at all.
      style={accentColor ? { borderLeftColor: accentColor, borderLeftWidth: '4px' } : undefined}
    >
      {/* ANN-01's optional image. Empty alt because the announcement's own title and body are
          directly beneath it: an organizer writes no alternative text, and a screen reader
          announcing a filename would be adding noise rather than meaning. */}
      {image && (
        <img
          src={image}
          alt=""
          className="max-h-56 w-full bg-brand-wash object-cover"
          loading="lazy"
          decoding="async"
        />
      )}

      <div className="p-4">
        <div className="flex items-start gap-2">
          {/* ANN-02's other half: whoever is reading a targeted announcement is told who it is
              for, so nobody has to guess whether it was meant for them. */}
          {audienceLabel && (
            <span className="text-chip rounded-full bg-sand-2 px-2.5 py-1 font-semibold text-ink-2">
              {audienceLabel}
            </span>
          )}
          <p className="text-subtitle ml-auto flex shrink-0 items-center gap-1.5 pt-0.5 text-ink-3">
            {isUnread && (
              <>
                <span className="size-2 rounded-full bg-brand" aria-hidden="true" />
                <span className="sr-only">Unread.</span>
              </>
            )}
            {formatFeedTimestamp(announcement.sentAt, timezone, now)}
          </p>
        </div>

        <h2 className="font-display text-card-title mt-2 font-semibold text-ink">
          {announcement.title}
        </h2>
        <p className="text-body mt-1.5 whitespace-pre-line text-ink-2">{announcement.body}</p>

        {ctaLabel && ctaUrl && (
          <a
            href={ctaUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-button rounded-button mt-4 inline-flex min-h-touch items-center gap-1.5 border border-border-input bg-surface px-4 font-semibold text-ink"
          >
            {ctaLabel}
            <Icon name="external" size={12} className="text-hairline" />
          </a>
        )}
      </div>
    </article>
  );
}
